import * as React from 'react';

import ActivityPack from './activity_pack';

import { requestGet } from '../../../../../modules/request';
import {
  Spinner,
  DropdownInput,
  EXTRA_SMALL_ICON_BASE_SRC,
  defaultSnackbarTimeout,
  getPageTitle,
  Snackbar,
} from '../../../../Shared/index';
import useSnackbarMonitor from '../../../../Shared/hooks/useSnackbarMonitor';
import { MY_ACTIVITIES_FEATURED_BLOG_ID } from '../../../constants/featuredBlogPost';
import getParameterByName from '../../modules/get_parameter_by_name';
import { PROGRESS_REPORTS_SELECTED_CLASSROOM_ID } from '../../progress_reports/progress_report_constants';
import ArticleSpotlight from '../../shared/articleSpotlight';

const ALL = 'ALL';
const groupSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/group.svg`;
const ALL_OPTION = {
  label: `<p><img alt="" src=${groupSrc} /><span>All Classes</span></p>`,
  value: ALL,
};

interface Classroom {
  code: string;
  grade: string;
  id: number;
  name: string;
  updated_at: string;
}

interface RawUnit {
  activity_classification_id: string;
  activity_id: string;
  activity_name: string;
  activity_uid: string;
  array_length: string;
  class_name: string;
  classroom_activity_created_at: string;
  classroom_activity_id: string;
  classroom_id: string;
  class_size: string;
  completed_count: string;
  due_date: string;
  number_of_assigned_students?: string;
  open?: boolean;
  publish_date?: string;
  scheduled?: boolean;
  staggered?: boolean;
  owner_name?: string;
  owned_by_current_user?: boolean;
  unit_activity_created_at?: string;
  unit_activity_id?: number;
  unit_created_at: string;
  unit_id: string;
  unit_name: string;
}

interface ClassroomInfo {
  assignedStudentCount: string | number;
  name: string;
  totalStudentCount: string | number;
}

interface ClassroomActivity {
  activityClassificationId: string;
  activityId: string;
  created_at: string;
  cuId: string;
  classroomId: string;
  dueDate: string;
  name: string;
  ownerName?: string;
  ownedByCurrentUser?: boolean;
  publishDate?: string;
  scheduled?: boolean;
  staggered?: boolean;
  uaId?: string;
}

interface CaUnit {
  classroomActivities: Map<string, ClassroomActivity>;
  classrooms: ClassroomInfo[];
  open?: boolean;
  unitCreated: string;
  unitId: string;
  unitName: string;
}

interface ManageUnitsProps {
  open: boolean;
}

export default function ManageUnits({ open }: ManageUnitsProps) {
  const [allUnits, setAllUnits] = React.useState<CaUnit[]>([]);
  const [classrooms, setClassrooms] = React.useState<Classroom[]>([]);
  const [loaded, setLoaded] = React.useState(false);
  const [selectedClassroomId, setSelectedClassroomId] = React.useState<number | string>(
    Number(getParameterByName('classroom_id')) || ALL
  );
  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const [snackbarCopy, setSnackbarCopy] = React.useState<string | null>(null);

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout);

  React.useEffect(() => {
    document.title = getPageTitle(
      'Manage Activities',
      `My ${open ? 'Open' : 'Closed'} Activity Packs`
    );
  }, [open]);

  React.useEffect(() => {
    getClassrooms();
    getRecommendationIds();

    const handlePopState = () => {
      setLoaded(false);
      setSelectedClassroomId(Number(getParameterByName('classroom_id')));
      getClassrooms();
    };

    window.onpopstate = handlePopState;
    return () => {
      window.onpopstate = null;
    };
  }, []);

  function hashLinkScroll() {
    const { hash } = window.location;
    if (hash) {
      const id = hash.replace('#', '');
      document.getElementById(id)?.scrollIntoView();
    }
  }

  function generateNewCaUnit(u: RawUnit): CaUnit {
    const classroom: ClassroomInfo = {
      assignedStudentCount: u.number_of_assigned_students ?? u.class_size,
      name: u.class_name,
      totalStudentCount: u.class_size,
    };

    const caObj: CaUnit = {
      classroomActivities: new Map(),
      classrooms: [classroom],
      open: u.open,
      unitCreated: u.unit_created_at,
      unitId: u.unit_id,
      unitName: u.unit_name,
    };

    caObj.classroomActivities.set(u.activity_id, {
      activityClassificationId: u.activity_classification_id,
      activityId: u.activity_id,
      created_at: u.unit_activity_created_at ?? u.unit_created_at,
      classroomId: u.classroom_id,
      cuId: u.classroom_activity_id,
      dueDate: u.due_date,
      name: u.activity_name,
      ownerName: u.owner_name,
      ownedByCurrentUser: u.owned_by_current_user,
      publishDate: u.publish_date,
      scheduled: u.scheduled,
      staggered: u.staggered,
      uaId: u.unit_activity_id,
    });

    return caObj;
  }

  function orderUnits(units: Record<string, CaUnit>): CaUnit[] {
    return Object.values(units);
  }

  function parseUnits(data: RawUnit[]): CaUnit[] {
    const parsedUnits: Record<string, CaUnit> = {};
    data.forEach(u => {
      if (!parsedUnits[u.unit_id]) {
        parsedUnits[u.unit_id] = generateNewCaUnit(u);
      } else {
        const caUnit = parsedUnits[u.unit_id];
        if (!caUnit.classrooms.some(c => c.name === u.class_name)) {
          caUnit.classrooms.push({
            assignedStudentCount: u.number_of_assigned_students ?? u.class_size,
            name: u.class_name,
            totalStudentCount: u.class_size,
          });
        }
        caUnit.classroomActivities.set(
          u.activity_id,
          caUnit.classroomActivities.get(u.activity_id) || {
            activityClassificationId: u.activity_classification_id,
            activityId: u.activity_id,
            created_at: u.unit_activity_created_at ?? u.unit_created_at,
            classroomId: u.classroom_id,
            cuId: u.classroom_activity_id,
            dueDate: u.due_date,
            name: u.activity_name,
            ownerName: u.owner_name,
            ownedByCurrentUser: u.owned_by_current_user,
            publishDate: u.publish_date,
            scheduled: u.scheduled,
            staggered: u.staggered,
            uaId: u.unit_activity_id,
          }
        );
      }
    });

    return orderUnits(parsedUnits);
  }

  function getClassrooms() {
    requestGet(
      `${window.postbuildEnv.DEFAULT_URL}/teachers/classrooms/classrooms_i_teach`,
      body => {
        const fetched = body.classrooms as Classroom[];
        const stored = window.localStorage.getItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID);
        const match = fetched.find(c => c.id === Number(stored));
        if ((selectedClassroomId === ALL || !selectedClassroomId) && match) {
          setSelectedClassroomId(match.id);
        }
        if (fetched.length) {
          setClassrooms(fetched);
          getUnits();
        } else {
          setLoaded(true);
        }
      }
    );
  }

  function getRecommendationIds() {
    fetch(
      `${window.postbuildEnv.DEFAULT_URL}/teachers/progress_reports/activity_with_recommendations_ids`,
      { credentials: 'include', method: 'GET', mode: 'cors' }
    )
      .then(res => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .catch(() => {});
  }

  function getUnits() {
    requestGet(
      `${window.postbuildEnv.DEFAULT_URL}/teachers/units`,
      body => {
        const parsed = parseUnits(body);
        setAllUnits(parsed);
        setLoaded(true);
        hashLinkScroll();

      }
    );
  }

  function showSnackbarMsg(text: string) {
    setShowSnackbar(true);
    setSnackbarCopy(text);
  }

  function switchClassrooms(opt: { label: string; value: number|string }) {
    window.localStorage.setItem(
      PROGRESS_REPORTS_SELECTED_CLASSROOM_ID,
      String(opt.value)
    );
    const base = `/teachers/classrooms/activity_planner${open ? '' : '/closed'}`;
    window.history.pushState(
      {},
      '',
      opt.value && opt.value !== ALL ? `${base}?classroom_id=${opt.value}` : base
    );
    setSelectedClassroomId(opt.value);
  }

  const dropdownOptions = [
    ALL_OPTION,
    ...classrooms.map(c => ({
      label: `<p><img alt=\"\" src=${groupSrc} /><span>${c.name}</span></p>`,
      value: c.id,
    })),
  ];

  const displayable = allUnits
    .filter(u => {
      if (selectedClassroomId === ALL) { return true }

      const selectedClassroom = classrooms.find(c => c.id === Number(selectedClassroomId));
      return u.classrooms.find(c => c.name === selectedClassroom.name)
    })
    .filter(u => u.open === open);

  if (!loaded) {
    return <Spinner />;
  }

  return (
    <React.Fragment>
      <main
        className="my-activities gray-background-accommodate-footer"
        id="post-secondary-nav"
      >
        <Snackbar text={snackbarCopy ?? ''} visible={showSnackbar} />
        <section className="my-activities-header">
          <div className="container">
            <div>
              <h1>My {open ? 'Open' : 'Closed'} Activity Packs</h1>
              <div>
                <DropdownInput
                  ariaLabel="Classroom"
                  className="medium bordered icon focus-on-light"
                  handleChange={switchClassrooms}
                  isSearchable={false}
                  options={dropdownOptions}
                  usesCustomOption={true}
                  value={dropdownOptions.find(
                    opt => opt.value === selectedClassroomId
                  )}
                />
                <a
                  className="quill-button contained medium green focus-on-light"
                  href="/assign"
                >
                  Assign activities
                </a>
              </div>
            </div>
          </div>
        </section>

        {displayable.length ? (
          <section className="activity-packs container">
            {displayable.map(unit => (
              <ActivityPack
                data={unit}
                getUnits={getUnits}
                key={unit.unitId}
                selectedClassroomId={selectedClassroomId}
                showSnackbar={showSnackbarMsg}
              />
            ))}
          </section>
        ) : (
          <div className="my-activities-empty-state container">
            <h2>
              {open
                ? 'Start by assigning activities'
                : 'You have no closed activity packs'}
            </h2>
            <p>
              {open
                ? 'Nothing to see here yet! Once you assign activities, they will show up here.'
                : 'Closed activity packs are hidden from students. You can still access them in your reports.'}
            </p>
          </div>
        )}
      </main>
      {open && <ArticleSpotlight blogPostId={MY_ACTIVITIES_FEATURED_BLOG_ID} />}
    </React.Fragment>
  );
}
